import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react'
import { fromJS, List, Map } from 'immutable'
import _get from 'lodash/get'
import { connect as connectRedux } from 'react-redux'
import FormsyForm from 'formsy-react'
import { getBoundActions } from '../../actions/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../Loading/index'
import GaiaLogo, { TYPE_TEAL } from '../GaiaLogo/index'
import ErrorMessage from '../ErrorMessage/index'
import { EmailInput } from '../FormInput.v2/index'
import { H2, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import { FORM_INPUT_NAME_EMAIL } from '../../services/form/index'
import { LOGIN_GA4_EVENT, LOG_IN_EMAIL_REQUESTED_GA4_EVENT } from '../../services/event-tracking/index'
import { getPrimary } from '../../services/languages/index'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import {
  MAGIC_LINK_CONSUME_TRUE,
  MAGIC_LINK_EMAIL_MODE_LINK_ONLY,
  MAGIC_LINK_FLOW_WEB_LOGIN,
} from '../../services/remote-login-code/index'
import { stringify } from '../../services/query-string/index'
import { URL_HOME, URL_LOGIN_WITH_LINK } from '../../services/url/constants'
import { isFullUrl } from '../../services/url/index'

export function getSafeRedirectUrl (redirect) {
  if (typeof redirect !== 'string' || redirect.length === 0) {
    return URL_HOME
  }
  let decodedRedirect = redirect
  try {
    decodedRedirect = decodeURIComponent(redirect)
  } catch (e) {
    // Ignore malformed redirects and fall back to the homepage below.
  }
  if (
    !decodedRedirect
    || isFullUrl(decodedRedirect)
    || decodedRedirect.startsWith('//')
    || !decodedRedirect.startsWith('/')
  ) {
    return URL_HOME
  }
  return decodedRedirect
}

export function LoginWithLinkPage (props) {
  const {
    history,
    location,
    staticText,
    loginStaticText,
    user,
    setEventDefault,
    approvePrivateLoginCode,
    requestMagicLink,
  } = props

  const privateCode = _get(location, ['query', 'privateCode']) === 'true' && _get(location, ['query', 'code'])
  const shouldConsumeApprovedCode = _get(location, ['query', 'consume']) === MAGIC_LINK_CONSUME_TRUE
    || _get(location, ['query', 'flow']) === MAGIC_LINK_FLOW_WEB_LOGIN
  const redirectUrl = useMemo(() => {
    return getSafeRedirectUrl(_get(location, ['query', 'redirect']))
  }, [location])
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))

  const [canSubmit, setCanSubmit] = useState(false)
  const [approvalComplete, setApprovalComplete] = useState(false)
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  const [showRecovery, setShowRecovery] = useState(!privateCode)
  const [requestSentEmail, setRequestSentEmail] = useState(null)
  const [isLoading, setIsLoading] = useState(!!privateCode)

  const clearMagicLinkQueryFromHistory = useCallback(() => {
    if (!history || typeof history.replace !== 'function') {
      return
    }
    const cleanedQuery = {}
    const redirect = _get(location, ['query', 'redirect'])
    const consume = _get(location, ['query', 'consume'])
    const flow = _get(location, ['query', 'flow'])
    if (redirect) {
      cleanedQuery.redirect = redirect
    }
    if (consume) {
      cleanedQuery.consume = consume
    }
    if (flow) {
      cleanedQuery.flow = flow
    }
    const queryString = stringify(cleanedQuery)
    history.replace(`${URL_LOGIN_WITH_LINK}${queryString ? `?${queryString}` : ''}`)
  }, [history, location])

  const showRecoveryState = useCallback(() => {
    clearMagicLinkQueryFromHistory()
    setErrorMessage(null)
    setRequestSentEmail(null)
    setShowRecovery(true)
    setIsLoading(false)
  }, [clearMagicLinkQueryFromHistory])

  const handlePrivateCode = useCallback(async () => {
    let shouldStopLoading = true
    try {
      const { success, auth } = await approvePrivateLoginCode({
        privateCode,
        consumeApprovedCode: shouldConsumeApprovedCode,
      })
      if (!success) {
        showRecoveryState()
        return
      }
      setEventDefault(LOGIN_GA4_EVENT.mergeDeep({
        ui_location: 'magic link',
      }))
      if (!shouldConsumeApprovedCode) {
        setApprovalComplete(true)
        return
      }
      shouldStopLoading = false
      historyRedirect({
        auth: fromJS(auth || {}),
        history,
        historyMethod: HISTORY_METHOD_REPLACE,
        language: userLanguage,
        timeout: 0,
        url: redirectUrl,
      })
    } catch (e) {
      showRecoveryState()
    } finally {
      if (shouldStopLoading) {
        setIsLoading(false)
      }
    }
  }, [
    approvePrivateLoginCode,
    history,
    privateCode,
    redirectUrl,
    setEventDefault,
    showRecoveryState,
    shouldConsumeApprovedCode,
    userLanguage,
  ])

  const onEmailSubmit = useCallback(async (formData) => {
    setEventDefault(LOG_IN_EMAIL_REQUESTED_GA4_EVENT)
    setIsLoading(true)
    try {
      const requestedEmail = _get(formData, 'email', '')
      const res = await requestMagicLink({
        email: requestedEmail,
        language: userLanguage,
        emailMode: MAGIC_LINK_EMAIL_MODE_LINK_ONLY,
        consumeOnClick: true,
        redirect: redirectUrl !== URL_HOME ? redirectUrl : undefined,
      })
      if (res.success) {
        clearMagicLinkQueryFromHistory()
        setEmail(requestedEmail)
        setRequestSentEmail(requestedEmail)
        setErrorMessage(null)
        return
      }
      setErrorMessage(
        res.message === 'emailNotExists'
          ? loginStaticText.get('emailNotExists')
          : staticText.get('requestError'),
      )
    } catch (e) {
      setErrorMessage(staticText.get('requestError'))
    } finally {
      setIsLoading(false)
    }
  }, [
    clearMagicLinkQueryFromHistory,
    loginStaticText,
    requestMagicLink,
    setEventDefault,
    staticText,
    userLanguage,
    redirectUrl,
  ])

  let content = (
    <p className="login-with-link-page__message">{staticText.get('loggingIn')}</p>
  )

  if (isLoading) {
    content = <>
      <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      <p className="login-with-link-page__hint">{staticText.get('loggingIn')}</p>
    </>
  } else if (requestSentEmail) {
    content = <>
      <p className="login-with-link-page__message">{staticText.get('successMessage')}</p>
      <p className="login-with-link-page__message login-with-link-page__message--email">{requestSentEmail}</p>
      <p className="login-with-link-page__message">{staticText.get('successTip')}</p>
      <Button
        type={TYPES.SECONDARY}
        className="login-with-link-page__button"
        url={URL_HOME}
      >
        {staticText.get('goToGaia')}
      </Button>
    </>
  } else if (approvalComplete) {
    content = <>
      <p className="login-with-link-page__message">{staticText.get('legacySuccessMessage')}</p>
      <p className="login-with-link-page__message">{staticText.get('legacySuccessTip')}</p>
    </>
  } else if (showRecovery) {
    content = <div className="login login-with-link-page__login">
      <H2 as={HEADING_TYPES.H4} className="login__title">
        {staticText.get('errorMessage')}
      </H2>
      <p className="login-with-link-page__message">{staticText.get('errorTip')}</p>
      <FormsyForm
        onValidSubmit={onEmailSubmit}
        onValid={() => setCanSubmit(true)}
        onInvalid={() => setCanSubmit(false)}
      >
        <EmailInput
          className="login-username__username"
          disabled={isLoading}
          validations={{ isEmail: true }}
          validationErrors={{ isEmail: loginStaticText.get('emailValidError') }}
          validationTimeout={750}
          label={loginStaticText.get('email')}
          name={FORM_INPUT_NAME_EMAIL}
          tabIndex="1"
          value={email}
          required
          autofocus
          autocomplete="email"
        />
        {errorMessage ? <ErrorMessage errorMessages={[errorMessage]} /> : null}
        <div className="login__submit-wrapper">
          <Button
            type={TYPES.PRIMARY}
            submit
            className="login__login-code-button"
            disabled={!canSubmit}
            stacked
          >
            {staticText.get('sendLink')}
          </Button>
        </div>
      </FormsyForm>
    </div>
  }

  useEffect(() => {
    if (privateCode && !showRecovery) {
      handlePrivateCode()
    } else {
      setIsLoading(false)
    }
  }, [handlePrivateCode, privateCode, showRecovery])

  return (
    <div className="login-with-link-page">
      <GaiaLogo type={TYPE_TEAL} isHref/>
      <div className="login-with-link-page__content">
        {content}
      </div>
    </div>
  )
}

export default connectRedux(({
  staticText,
  user,
}) => ({
  staticText: staticText.getIn(['data', 'loginWithLinkPage', 'data'], Map()),
  loginStaticText: staticText.getIn(['data', 'login', 'data'], Map()),
  user,
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    approvePrivateLoginCode: actions.remoteLoginCode.doApprovePrivateLoginCode,
    requestMagicLink: actions.remoteLoginCode.doRequestMagicLink,
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(LoginWithLinkPage)
